import { T, Var } from 'gt-react';
import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAPInvoiceFamousVouchers } from '@/api/rpc/hooks/accounts-payable/useAPInvoiceFamousVouchers';
import { useCan } from '@/context/authz';
import { formatCurrency } from '@/utils/formatters';
import { Action, Subject } from '@erp/authz';
import type { ListAPInvoiceFamousVouchersResponse } from '@erp/rpc';
import { FamousVoucherCard, type FormatMoney } from './FamousVoucherCard';

type Vouchers = ListAPInvoiceFamousVouchersResponse;

function UnvoucheredList({
  vouchers,
  money,
}: {
  vouchers: Vouchers['vouchers'];
  money: FormatMoney;
}) {
  if (vouchers.length === 0) return null;
  return (
    <details className="text-xs text-muted-foreground">
      <summary className="cursor-pointer">
        <T context="collapsed list of Famous vouchers voided to $0">
          <Var>{vouchers.length}</Var> unvouchered ($0) vouchers
        </T>
      </summary>
      <ul className="mt-1 space-y-0.5 pl-4 opacity-70">
        {vouchers.map((voucher) => (
          <li key={voucher.voucherNumber}>
            <T>
              Voucher <Var>{voucher.voucherNumber}</Var>
            </T>
            {voucher.stubDescription && (
              <>
                {' · '}
                <T context="Famous voucher stub description">
                  Stub: <Var>{voucher.stubDescription}</Var>
                </T>
              </>
            )}
            {' · '}
            {money(voucher.amount)}
          </li>
        ))}
      </ul>
    </details>
  );
}

function VouchersBody({ data, money }: { data: Vouchers; money: FormatMoney }) {
  const counted = data.vouchers.filter((v) => v.state !== 'unvouchered');
  const unvouchered = data.vouchers.filter((v) => v.state === 'unvouchered');
  return (
    <div className="space-y-2">
      {data.vouchers.length === 0 && (
        <div className="text-sm text-muted-foreground">
          <T context="no Famous voucher was found for the invoice; the searched invoice numbers follow">
            No Famous voucher found for this invoice (searched{' '}
            <Var>{data.searchedInvoiceNumbers.join(', ')}</Var>).
          </T>
        </div>
      )}
      {counted.map((voucher) => (
        <FamousVoucherCard
          key={voucher.voucherNumber}
          voucher={voucher}
          invoiceTotal={data.invoiceTotal}
          money={money}
        />
      ))}
      <UnvoucheredList vouchers={unvouchered} money={money} />
    </div>
  );
}

/**
 * The Famous vouchers for an AP invoice, read live from Famous: each
 * voucher's amount, status and stub, and its lines grouped by load / order /
 * PO, with a flag when it doesn't cover every file on the invoice. Hidden for
 * invoices whose supplier isn't a Famous vendor. If Famous can't be reached it
 * says so and the rest of the page is unaffected.
 */
export function FamousVouchersSection({
  invoiceId,
  currency,
}: {
  invoiceId: string;
  currency?: string | null;
}) {
  const [canRead] = useCan(Action.Read, Subject.APInvoice);
  const { data, isPending, isError, isFetching, refetch } =
    useAPInvoiceFamousVouchers(invoiceId, canRead);
  const money: FormatMoney = (amount) =>
    formatCurrency(Number(amount), { currency: currency || 'USD' });

  if (!canRead || data?.status === 'not_applicable') return null;

  const unavailable = isError || data?.status === 'unavailable';
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm">
          <T>Famous vouchers</T>
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          disabled={isFetching}
          onClick={() => void refetch()}
        >
          <ArrowClockwiseIcon className="h-3.5 w-3.5" />
          <T>Refresh</T>
        </Button>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <div className="text-sm text-muted-foreground">
            <T>Loading Famous vouchers...</T>
          </div>
        ) : unavailable || !data ? (
          <div className="text-sm text-muted-foreground">
            <T context="Famous could not be reached to read vouchers; the user can retry">
              Couldn't reach Famous to read this invoice's vouchers. Try Refresh
              in a moment.
            </T>
          </div>
        ) : (
          <VouchersBody data={data} money={money} />
        )}
      </CardContent>
    </Card>
  );
}
